import React, { useCallback, useMemo } from 'react';

interface MbtiScores {
  ei: number;
  sn: number;
  tf: number;
  jp: number;
}

interface PersonalModelProps {
  sanValue: number;
  sanMax: number;
  sanStatus: string;
  sanStatusIcon: string;
  spirituality: number;
  spiritualityMax: number;
  spiritLevel: number;
  spiritProgress: number;
  techExp: number;
  techSequence: number;
  healthHp: number;
  healthStreak: number;
  anchorPoints: number;
  nickname?: string;
  mbtiType?: string;
  sequenceLevel?: number;
  mbtiScores?: MbtiScores;
  dimensionScores?: Record<string, number>;
}

const CHART_SIZE = 340;
const CHART_CENTER = CHART_SIZE / 2;
const OUTER_RADIUS = 118;
const NODE_RADIUS = 136;
const ANGLES = [-90, -30, 30, 90, 150, 210];

const clampScore = (value: number) => Math.min(100, Math.max(0, Math.round(value || 0)));

const polarPoint = (angle: number, radius: number) => {
  const rad = (angle * Math.PI) / 180;
  return {
    x: CHART_CENTER + Math.cos(rad) * radius,
    y: CHART_CENTER + Math.sin(rad) * radius,
  };
};

const pointList = (points: Array<{ x: number; y: number }>) =>
  points.map(point => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');

const describeScore = (value: number) => {
  if (value >= 80) return '耀光';
  if (value >= 60) return '点亮';
  if (value >= 35) return '成形';
  if (value > 0) return '待补';
  return '沉睡';
};

const PersonalModel: React.FC<PersonalModelProps> = ({
  sanValue,
  sanMax,
  sanStatus,
  spirituality,
  spiritualityMax,
  spiritLevel,
  spiritProgress,
  techExp,
  techSequence,
  healthHp,
  healthStreak,
  anchorPoints,
  mbtiType,
  mbtiScores,
  dimensionScores,
}) => {
  const mbti = (mbtiType || '').toUpperCase();
  const hasMbti = /^[EI][SN][TF][JP]$/.test(mbti);
  const healthPct = clampScore(healthHp);
  const sanPct = sanMax > 0 ? clampScore((sanValue / sanMax) * 100) : 0;
  const techPct = clampScore(techExp / 2);
  const bondPct = clampScore(anchorPoints * 10);

  const wisdomPct = useMemo(() => {
    if (!hasMbti) return 18;
    const scores = [mbtiScores?.ei, mbtiScores?.sn, mbtiScores?.tf, mbtiScores?.jp]
      .map(score => (typeof score === 'number' ? Math.abs(score - 50) * 2 : 35));
    return clampScore(scores.reduce((sum, score) => sum + score, 0) / scores.length);
  }, [hasMbti, mbtiScores]);

  const getDimensionScore = useCallback((primaryKey: string, fallback: number, legacyKey?: string) => {
    const raw = dimensionScores?.[primaryKey] ?? (legacyKey ? dimensionScores?.[legacyKey] : undefined);
    return typeof raw === 'number' && !Number.isNaN(raw) ? clampScore(raw) : clampScore(fallback);
  }, [dimensionScores]);

  const dimensions = useMemo(() => [
    {
      key: 'body',
      label: '身体',
      glyph: '◇',
      value: getDimensionScore('body', healthPct),
      accent: '#22c55e',
      meta: `HP ${healthHp}/100 · 连续${healthStreak}天`,
    },
    {
      key: 'heart',
      label: '情绪',
      glyph: '♥',
      value: getDimensionScore('heart', sanPct, 'mind'),
      accent: '#f472b6',
      meta: sanStatus,
    },
    {
      key: 'purpose',
      label: '方向',
      glyph: '⌖',
      value: getDimensionScore('purpose', spiritProgress),
      accent: '#a855f7',
      meta: `灵性 Lv.${spiritLevel}`,
    },
    {
      key: 'craft',
      label: '技艺',
      glyph: '✦',
      value: getDimensionScore('craft', techPct),
      accent: '#22d3ee',
      meta: `序列${techSequence} · ${techExp} EXP`,
    },
    {
      key: 'bond',
      label: '联结',
      glyph: '∞',
      value: getDimensionScore('bond', bondPct, 'social'),
      accent: '#facc15',
      meta: `${anchorPoints} 个锚点`,
    },
    {
      key: 'intellect',
      label: '知识',
      glyph: '☉',
      value: getDimensionScore('intellect', wisdomPct),
      accent: '#60a5fa',
      meta: hasMbti ? `${mbti} 认知轮廓` : '待完成 MBTI',
    },
  ], [
    anchorPoints,
    bondPct,
    getDimensionScore,
    hasMbti,
    healthHp,
    healthPct,
    healthStreak,
    mbti,
    sanPct,
    sanStatus,
    spiritLevel,
    spiritProgress,
    techExp,
    techPct,
    techSequence,
    wisdomPct,
  ]);

  const outerPoints = useMemo(() => ANGLES.map(angle => polarPoint(angle, OUTER_RADIUS)), []);
  const nodePoints = useMemo(() => ANGLES.map(angle => polarPoint(angle, NODE_RADIUS)), []);
  const valuePoints = useMemo(
    () => dimensions.map((dimension, index) => polarPoint(ANGLES[index], OUTER_RADIUS * (dimension.value / 100))),
    [dimensions],
  );
  const averageScore = clampScore(dimensions.reduce((sum, item) => sum + item.value, 0) / dimensions.length);
  const strongestDimension = dimensions.reduce((best, item) => (item.value > best.value ? item : best), dimensions[0]);
  const weakestDimension = dimensions.reduce((low, item) => (item.value < low.value ? item : low), dimensions[0]);
  const awakenedCount = dimensions.filter(item => item.value >= 60).length;
  const spiritualityPct = spiritualityMax > 0 ? clampScore((spirituality / spiritualityMax) * 100) : 0;

  return (
    <section className="astro-wrap" aria-label="我的六维星盘">
      <div className="astro-stars" />
      <header className="astro-head">
        <div>
          <span className="astro-eyebrow">PERSONAL ASTROLABE</span>
          <h2>我的星盘</h2>
        </div>
        <span className="astro-live"><i />Lv.{spiritLevel}</span>
      </header>

      <div className="astro-stage">
        <div className="astro-orbit astro-orbit-a" />
        <div className="astro-orbit astro-orbit-b" />
        <svg className="astro-chart" viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`} role="img" aria-label={`六维星盘，综合${averageScore}分`}>
          <defs>
            <radialGradient id="astro-core" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(250,204,21,0.36)" />
              <stop offset="70%" stopColor="rgba(250,204,21,0.08)" />
              <stop offset="100%" stopColor="rgba(250,204,21,0)" />
            </radialGradient>
            <linearGradient id="astro-fill" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(244,114,182,0.52)" />
              <stop offset="48%" stopColor="rgba(250,204,21,0.34)" />
              <stop offset="100%" stopColor="rgba(34,211,238,0.42)" />
            </linearGradient>
            <filter id="astro-glow">
              <feGaussianBlur stdDeviation="4" result="glow" />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {[0.25, 0.5, 0.75, 1].map(step => (
            <polygon
              key={step}
              points={pointList(ANGLES.map(angle => polarPoint(angle, OUTER_RADIUS * step)))}
              className="astro-grid"
            />
          ))}
          {outerPoints.map((point, index) => (
            <line key={index} x1={CHART_CENTER} y1={CHART_CENTER} x2={point.x} y2={point.y} className="astro-axis" />
          ))}
          <polygon points={pointList([outerPoints[0], outerPoints[2], outerPoints[4]])} className="astro-star" />
          <polygon points={pointList([outerPoints[1], outerPoints[3], outerPoints[5]])} className="astro-star" />
          <polygon points={pointList(outerPoints)} className="astro-hex" />
          <polygon points={pointList(valuePoints)} className="astro-value" filter="url(#astro-glow)" />

          {valuePoints.map((point, index) => (
            <g key={dimensions[index].key}>
              <line
                x1={outerPoints[index].x}
                y1={outerPoints[index].y}
                x2={point.x}
                y2={point.y}
                className="astro-measure"
                style={{ stroke: dimensions[index].accent }}
              />
              <circle cx={point.x} cy={point.y} r="8" className="astro-value-halo" style={{ fill: dimensions[index].accent }} />
              <circle cx={point.x} cy={point.y} r="3.8" className="astro-value-dot" style={{ stroke: dimensions[index].accent }} />
            </g>
          ))}

          <circle cx={CHART_CENTER} cy={CHART_CENTER} r="58" className="astro-core-ring" />
          <circle cx={CHART_CENTER} cy={CHART_CENTER} r="38" className="astro-core-disc" />
        </svg>

        <div className="astro-core-copy">
          <strong>{averageScore}</strong>
        </div>

        {dimensions.map((dimension, index) => {
          const circumference = 2 * Math.PI * 19;
          const offset = circumference - circumference * (dimension.value / 100);
          return (
            <div
              key={dimension.key}
              className="astro-node"
              style={{
                left: `${nodePoints[index].x}px`,
                top: `${nodePoints[index].y}px`,
                '--accent': dimension.accent,
              } as React.CSSProperties}
              aria-label={`${dimension.label}${dimension.value}分`}
            >
              <svg className="astro-node-ring" viewBox="0 0 44 44" aria-hidden="true">
                <circle cx="22" cy="22" r="19" className="node-ring-base" />
                <circle
                  cx="22"
                  cy="22"
                  r="19"
                  className="node-ring-value"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                />
              </svg>
              <span className="astro-node-glyph">{dimension.glyph}</span>
              <strong>{dimension.value}</strong>
            </div>
          );
        })}
      </div>

      <div className="astro-legend" aria-label="六维数值">
        {dimensions.map(dimension => (
          <div className="legend-row" key={dimension.key}>
            <span className="legend-glyph" style={{ color: dimension.accent }}>{dimension.glyph}</span>
            <span className="legend-label">{dimension.label}</span>
            <div className="legend-track">
              <i style={{ width: `${dimension.value}%`, background: dimension.accent }} />
            </div>
            <strong style={{ color: dimension.accent }}>{dimension.value}</strong>
          </div>
        ))}
      </div>

      <div className="astro-chip-row">
        <span><i style={{ background: '#a855f7' }} />灵性 Lv.{spiritLevel}</span>
        <span><i style={{ background: '#facc15' }} />锚点 {anchorPoints}</span>
        <span><i style={{ background: '#34d399' }} />{awakenedCount}/6 点亮</span>
        <span><i style={{ background: '#60a5fa' }} />灵性 {spiritualityPct}%</span>
      </div>

      <section className="astro-oracle">
        <div>
          <span>星盘启示</span>
          <h3>{strongestDimension.label}最亮，{weakestDimension.label}待补全</h3>
          <p>{strongestDimension.meta}。{weakestDimension.label}仍处于{describeScore(weakestDimension.value)}状态。</p>
        </div>
        <div className="oracle-score">
          <strong>{averageScore}</strong>
          <small>综合</small>
        </div>
      </section>

      <style>{`
        .astro-wrap {
          position: relative;
          overflow: hidden;
          margin-bottom: 18px;
          border-radius: 28px;
          border: 1px solid rgba(250,204,21,0.16);
          background:
            radial-gradient(circle at 50% 8%, rgba(91,33,182,0.36), transparent 28%),
            radial-gradient(circle at 18% 72%, rgba(34,211,238,0.13), transparent 30%),
            radial-gradient(circle at 88% 58%, rgba(250,204,21,0.1), transparent 32%),
            linear-gradient(180deg, #050816 0%, #020617 100%);
          box-shadow: 0 24px 70px rgba(2,6,23,0.56);
          color: #f8fafc;
        }
        .astro-stars {
          position: absolute;
          inset: 0;
          opacity: 0.24;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,255,255,0.9) 0 1px, transparent 1.4px),
            radial-gradient(circle, rgba(250,204,21,0.75) 0 1px, transparent 1.4px),
            radial-gradient(circle, rgba(192,132,252,0.8) 0 1px, transparent 1.6px);
          background-size: 78px 78px, 126px 126px, 166px 166px;
          background-position: 12px 18px, 40px 82px, 88px 12px;
        }
        .astro-head {
          position: relative;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 18px 2px;
        }
        .astro-eyebrow {
          display: block;
          color: rgba(250,204,21,0.7);
          font: 700 9px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
          letter-spacing: 1.8px;
        }
        .astro-head h2 {
          margin: 5px 0 0;
          font-size: 19px;
          line-height: 1.1;
          font-weight: 850;
          letter-spacing: 0;
        }
        .astro-live {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 34px;
          padding: 0 11px;
          border-radius: 999px;
          border: 1px solid rgba(250,204,21,0.18);
          background: rgba(2,6,23,0.46);
          color: rgba(226,232,240,0.74);
          font-size: 12px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .astro-live i {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #34d399;
          box-shadow: 0 0 14px rgba(52,211,153,0.82);
        }
        .astro-stage {
          position: relative;
          z-index: 1;
          width: 340px;
          height: 340px;
          margin: 4px auto 0;
        }
        .astro-chart {
          position: relative;
          z-index: 2;
          width: 340px;
          height: 340px;
          display: block;
        }
        .astro-orbit {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          pointer-events: none;
        }
        .astro-orbit-a {
          width: 268px;
          height: 268px;
          border: 1px solid rgba(250,204,21,0.08);
          box-shadow: 0 0 32px rgba(168,85,247,0.12);
        }
        .astro-orbit-b {
          width: 212px;
          height: 212px;
          border: 1px dashed rgba(168,85,247,0.18);
        }
        .astro-grid {
          fill: none;
          stroke: rgba(250,204,21,0.18);
          stroke-width: 1;
        }
        .astro-axis {
          stroke: rgba(148,163,184,0.12);
          stroke-width: 1;
        }
        .astro-star {
          fill: none;
          stroke: rgba(168,85,247,0.36);
          stroke-width: 1.2;
        }
        .astro-hex {
          fill: none;
          stroke: rgba(250,204,21,0.42);
          stroke-width: 1.25;
          stroke-dasharray: 3 5;
        }
        .astro-value {
          fill: url(#astro-fill);
          stroke: rgba(248,250,252,0.42);
          stroke-width: 1.3;
        }
        .astro-measure {
          opacity: 0.52;
          stroke-width: 1.8;
          stroke-linecap: round;
        }
        .astro-value-halo {
          opacity: 0.2;
        }
        .astro-value-dot {
          fill: #f8fafc;
          stroke-width: 1.8;
        }
        .astro-core-ring {
          fill: url(#astro-core);
          stroke: rgba(250,204,21,0.28);
          stroke-width: 1.2;
        }
        .astro-core-disc {
          fill: rgba(2,6,23,0.84);
          stroke: rgba(250,204,21,0.34);
          stroke-width: 1.1;
        }
        .astro-core-copy {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 4;
          transform: translate(-50%, -50%);
          text-align: center;
        }
        .astro-core-copy strong {
          display: block;
          color: #facc15;
          font: 900 39px/0.95 ui-monospace, SFMono-Regular, Menlo, monospace;
          text-shadow: 0 0 22px rgba(250,204,21,0.42);
        }
        .astro-core-copy span {
          display: block;
          margin-top: 5px;
          color: rgba(226,232,240,0.65);
          font-size: 11px;
          font-weight: 800;
        }
        .astro-node {
          position: absolute;
          z-index: 5;
          width: 58px;
          height: 58px;
          transform: translate(-50%, -50%);
          display: grid;
          place-items: center;
          border-radius: 50%;
          background:
            radial-gradient(circle at 50% 42%, color-mix(in srgb, var(--accent) 28%, transparent), transparent 58%),
            rgba(3,7,18,0.88);
          box-shadow: 0 0 18px color-mix(in srgb, var(--accent) 30%, transparent);
        }
        .astro-node-ring {
          position: absolute;
          inset: 0;
          width: 58px;
          height: 58px;
          transform: rotate(-90deg);
        }
        .node-ring-base {
          fill: none;
          stroke: rgba(148,163,184,0.14);
          stroke-width: 3.2;
        }
        .node-ring-value {
          fill: none;
          stroke: var(--accent);
          stroke-width: 3.2;
          stroke-linecap: round;
          transition: stroke-dashoffset 0.55s ease;
        }
        .astro-node-glyph {
          position: absolute;
          top: 10px;
          color: var(--accent);
          font-size: 15px;
          font-weight: 900;
          line-height: 1;
          text-shadow: 0 0 10px color-mix(in srgb, var(--accent) 70%, transparent);
        }
        .astro-node strong {
          position: absolute;
          bottom: 9px;
          color: #f8fafc;
          font: 900 17px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .astro-legend {
          position: relative;
          z-index: 2;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 10px;
          padding: 4px 18px 0;
        }
        .legend-row {
          min-width: 0;
          display: grid;
          grid-template-columns: 18px 34px minmax(0, 1fr) 28px;
          align-items: center;
          gap: 6px;
          padding: 8px 9px;
          border-radius: 12px;
          border: 1px solid rgba(148,163,184,0.09);
          background: rgba(15,23,42,0.5);
        }
        .legend-glyph {
          font-size: 14px;
          font-weight: 900;
          text-align: center;
        }
        .legend-label {
          color: rgba(226,232,240,0.84);
          font-size: 12px;
          font-weight: 800;
        }
        .legend-track {
          height: 5px;
          border-radius: 999px;
          overflow: hidden;
          background: rgba(30,41,59,0.82);
        }
        .legend-track i {
          display: block;
          height: 100%;
          border-radius: inherit;
          box-shadow: 0 0 10px currentColor;
        }
        .legend-row strong {
          text-align: right;
          font: 900 12px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .astro-chip-row {
          position: relative;
          z-index: 2;
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          padding: 14px 18px 0;
        }
        .astro-chip-row span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 28px;
          padding: 0 9px;
          border-radius: 999px;
          border: 1px solid rgba(148,163,184,0.1);
          background: rgba(2,6,23,0.44);
          color: rgba(226,232,240,0.76);
          font-size: 11px;
          font-weight: 750;
        }
        .astro-chip-row i {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }
        .astro-oracle {
          position: relative;
          z-index: 2;
          display: grid;
          grid-template-columns: minmax(0, 1fr) 70px;
          gap: 12px;
          align-items: center;
          margin: 14px 18px 18px;
          padding: 16px;
          border-radius: 18px;
          border: 1px solid rgba(250,204,21,0.18);
          background:
            radial-gradient(circle at 0% 0%, rgba(250,204,21,0.13), transparent 46%),
            linear-gradient(135deg, rgba(88,28,135,0.28), rgba(15,23,42,0.62));
        }
        .astro-oracle span {
          display: block;
          color: rgba(250,204,21,0.78);
          font-size: 11px;
          font-weight: 900;
          margin-bottom: 6px;
        }
        .astro-oracle h3 {
          margin: 0;
          color: rgba(248,250,252,0.96);
          font-size: 17px;
          line-height: 1.25;
          font-weight: 900;
        }
        .astro-oracle p {
          margin: 8px 0 0;
          color: rgba(203,213,225,0.72);
          font-size: 12px;
          line-height: 1.45;
        }
        .oracle-score {
          display: grid;
          place-items: center;
          align-content: center;
          min-height: 70px;
          border-radius: 17px;
          border: 1px solid rgba(250,204,21,0.22);
          background: rgba(2,6,23,0.62);
        }
        .oracle-score strong {
          color: #facc15;
          font: 900 30px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .oracle-score small {
          color: rgba(148,163,184,0.72);
          font-size: 11px;
          font-weight: 800;
        }
        @media (max-width: 370px) {
          .astro-stage {
            transform: scale(0.94);
            transform-origin: top center;
            margin-bottom: -20px;
          }
          .astro-legend {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

export default PersonalModel;
